import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

/**
 * ChipMonk theme: dark by default, light on request. The choice lives on
 * <html data-theme> (set before first paint by public/index.html) and in
 * localStorage("cm-theme"). Tailwind, the CSS tokens and MUI all follow it.
 */
const STORAGE_KEY = "cm-theme";

// Hex mirrors of brand/tokens.css. MUI needs real colours to compute hover/alpha shades.
export const PALETTE = {
  dark: {
    bg: "#0b0b0c", surface: "#141416", surface2: "#1c1c20", surface3: "#25252a",
    border: "#2a2a2f", borderStrong: "#706b63",
    text: "#f3eadb", muted: "#a39c90", faint: "#6f6a62",
    gold: "#c9a45c", goldHover: "#d8b56f", goldInk: "#0b0b0c", goldText: "#c9a45c",
    red: "#c8262c", success: "#2fa36b", warning: "#e0a43a", danger: "#ef5350", info: "#5b8def",
    chart: ["#b08838", "#4f82e3", "#1f9e86", "#d9434a", "#9a6ad6", "#c26a2a"],
    positive: "#1f9e86", negative: "#d9434a", neutral: "#3a3934", grid: "#232327", axis: "#6f6a62",
  },
  light: {
    bg: "#faf7f2", surface: "#ffffff", surface2: "#f3eee5", surface3: "#e9e2d6",
    border: "#e4ddd0", borderStrong: "#8e8476",
    text: "#17130e", muted: "#6b6358", faint: "#9c9386",
    gold: "#b8913f", goldHover: "#a88234", goldInk: "#17130e", goldText: "#8a6724",
    red: "#b01f25", success: "#1a7a4c", warning: "#94650f", danger: "#d32f2f", info: "#3565c8",
    chart: ["#a7781f", "#3565c8", "#15896f", "#c62d33", "#8250c4", "#b8621f"],
    positive: "#15896f", negative: "#c62d33", neutral: "#e6e1d7", grid: "#eee8dd", axis: "#9c9386",
  },
};

const readInitial = () => {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "light" || attr === "dark") return attr;
  }
  try {
    return localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
};

const ThemeContext = createContext({ mode: "dark", colors: PALETTE.dark, toggle: () => {}, setMode: () => {} });

const buildMuiTheme = (mode) => {
  const c = PALETTE[mode];
  return createTheme({
    palette: {
      mode,
      primary: { main: c.gold, dark: c.goldHover, light: c.goldHover, contrastText: c.goldInk },
      secondary: { main: c.red, contrastText: "#ffffff" },
      error: { main: c.danger },
      warning: { main: c.warning },
      success: { main: c.success },
      info: { main: c.info },
      background: { default: c.bg, paper: c.surface },
      text: { primary: c.text, secondary: c.muted, disabled: c.faint },
      divider: c.border,
      action: { hover: c.surface2, selected: mode === "dark" ? "rgba(201,164,92,0.14)" : "rgba(184,145,63,0.16)" },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      h1: { fontFamily: '"Montserrat", "Inter", sans-serif', fontWeight: 800 },
      h2: { fontFamily: '"Montserrat", "Inter", sans-serif', fontWeight: 800 },
      h3: { fontFamily: '"Montserrat", "Inter", sans-serif', fontWeight: 800 },
      h4: { fontFamily: '"Montserrat", "Inter", sans-serif', fontWeight: 800 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: { styleOverrides: { body: { backgroundColor: c.bg, color: c.text } } },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none", border: `1px solid ${c.border}` } },
      },
      MuiAppBar: { styleOverrides: { root: { border: 0 } } },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 20, boxShadow: "var(--cm-shadow-lg)" },
        },
      },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: "var(--cm-overlay)" } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: "1.125rem" } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 8, fontWeight: 600, minHeight: 36 } },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: c.surface2,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: c.borderStrong },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: c.muted },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: c.gold, borderWidth: 1 },
            "&.Mui-focused": { boxShadow: "var(--cm-glow-gold)" },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { "&.Mui-focused": { color: c.goldText } } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${c.border}` },
          head: { color: c.muted, fontWeight: 600, fontSize: "0.75rem", backgroundColor: c.surface },
        },
      },
      MuiTableRow: { styleOverrides: { root: { "&.MuiTableRow-hover:hover": { backgroundColor: c.surface2 } } } },
      MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 999 } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: c.surface3, color: c.text, border: `1px solid ${c.border}`, fontSize: "0.75rem" },
          arrow: { color: c.surface3 },
        },
      },
      MuiTabs: { styleOverrides: { indicator: { backgroundColor: c.gold, height: 3, borderRadius: 3 } } },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: "none", fontWeight: 600, color: c.muted, "&.Mui-selected": { color: c.text } },
        },
      },
      MuiMenu: { styleOverrides: { paper: { boxShadow: "var(--cm-shadow-md)" } } },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { "&.Mui-checked": { color: c.gold }, "&.Mui-checked + .MuiSwitch-track": { backgroundColor: c.gold } },
        },
      },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, backgroundColor: c.surface3 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
    },
  });
};

export const ChipMonkThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", PALETTE[mode].bg);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* storage unavailable: theme still applies for this session */
    }
  }, [mode]);

  const setMode = useCallback((m) => setModeState(m === "light" ? "light" : "dark"), []);
  const toggle = useCallback(() => setModeState((m) => (m === "dark" ? "light" : "dark")), []);
  const muiTheme = useMemo(() => buildMuiTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, colors: PALETTE[mode], toggle, setMode }), [mode, toggle, setMode]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline enableColorScheme />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useChipMonkTheme = () => useContext(ThemeContext);

const lightMuiTheme = buildMuiTheme("light");

/**
 * Forces light styling for everything inside, whatever the app theme:
 * printable things such as invoices and receipts are always paper-white.
 */
export const LightSurface = ({ children, className, style }) => (
  <div data-theme="light" className={className} style={{ color: "var(--cm-text)", ...style }}>
    <MuiThemeProvider theme={lightMuiTheme}>{children}</MuiThemeProvider>
  </div>
);
